import { getMonthName } from "@repo/shared";
import type { HistoricalStatistics } from "@repo/shared/types";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./period-returns-grid.i18n";
import { ReturnValue } from "./return-value";

type PeriodReturnsGridProps = {
  statistics: HistoricalStatistics;
  /** Jan–Dec per year, or Q1–Q4 per year (each compounded from its months). */
  mode: "months" | "quarters";
};

type Cell = { returnPct: number; partial: boolean } | undefined;

/**
 * One row per year, newest first, with the year's months or quarters and the
 * full year, in % with one decimal. Months with no data stay empty. Scrolls
 * inside its own container, header pinned, when there are many years.
 */
export function PeriodReturnsGrid({ statistics, mode }: PeriodReturnsGridProps) {
  const t = useT(T);
  const { userLanguage } = useUserLanguage();

  const columns =
    mode === "months"
      ? Array.from({ length: 12 }, (_, i) => ({ key: i + 1, label: getMonthName(i, userLanguage) }))
      : [1, 2, 3, 4].map((q) => ({ key: q, label: t.quarter(q) }));

  const byPeriod = new Map<string, Cell>(
    mode === "months"
      ? statistics.months.map((m) => [`${m.year}-${m.month}`, m])
      : statistics.quarters.map((q) => [`${q.year}-${q.quarter}`, q]),
  );
  const byYear = new Map(statistics.years.map((y) => [y.year, y]));
  const years = [...byYear.keys()].sort((a, b) => b - a);

  return (
    <Table variant="data" containerClassName="max-h-[420px] overflow-y-auto">
      <TableHeader className="sticky top-0 z-10">
        <TableRow>
          <TableHead className="w-16">
            {/* Blank in the design; the years name themselves. Kept for screen readers. */}
            <span className="sr-only">{t.year}</span>
          </TableHead>
          {columns.map((c) => (
            <TableHead key={c.key} className="text-right">
              {c.label}
            </TableHead>
          ))}
          <TableHead className="text-right">{t.fullYear}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {years.map((year) => {
          const full = byYear.get(year);
          return (
            <TableRow key={year}>
              <TableCell className="font-medium">{year}</TableCell>
              {columns.map((c) => {
                const cell = byPeriod.get(`${year}-${c.key}`);
                return (
                  <TableCell key={c.key} className="text-right">
                    {cell && <ReturnValue value={cell.returnPct} partial={cell.partial} />}
                  </TableCell>
                );
              })}
              <TableCell className="text-right font-semibold">
                {full && <ReturnValue value={full.returnPct} partial={full.partial} />}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
